import React, { useEffect, useState } from 'react'
import { LazyAvatar } from '@/components/common/LazyAvatar'
import { getCTALabel } from '@/components/mediaBuying/adCreatives/screens/adContent/CTADropdown'
import iconPlaceholder from '@/assets/images/media-buying/icon-placeholder.png'
import centerMockPhone from '@/assets/images/media-buying/center-mock-phone.png'
import videoMockPhone from '@/assets/images/media-buying/video-mock-phone.png'
import carouselMockPhone from '@/assets/images/media-buying/carousel-mock-phone.png'
import bookNowMockPhone from '@/assets/images/media-buying/book-now-mock-phone.png'
import sponsorIcon from '@/assets/images/media-buying/sponsor-icon.png'
import shareLikeMockPhone from '@/assets/images/media-buying/share-like-mock-phone.png'
import bottomBarMockPhone from '@/assets/images/media-buying/bottom-bar-mock-phone.png'
import { Media } from '@/components/mediaBuying/adCreatives/screens/mediaCrop/Media.jsx'
import { constructProxyUrl } from '@/utils/proxy.js'
import { getFormatMediaProps } from './getFormatMediaProps'
import { ellipses } from '@/utils/tools.jsx'
import { cn } from '@/lib/utils'

const CarouselPreview = ({ assets: passedAssets, cta, autoPlay = false, playWithSound = false }) => {
    const [currentIndex, setCurrentIndex] = useState(0)
    const [assets] = useState(
        passedAssets?.map((asset) => (asset = { ...asset, ...{ url: constructProxyUrl(asset.url) } })) || [],
    )

    const handlePrevious = (e) => {
        e.stopPropagation()
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : assets?.length - 1))
    }

    const handleNext = (e) => {
        e.stopPropagation()
        setCurrentIndex((prev) => (prev < assets?.length - 1 ? prev + 1 : 0))
    }

    // Show 2 cards starting from currentIndex
    const visibleAssets = assets.slice(currentIndex, currentIndex + 1)

    return (
        <div className="tw:w-full tw:h-full tw:bg-white tw:relative tw:flex tw:flex-col">
            {/* Navigation Arrows */}
            {assets.length > 1 && (
                <>
                    <button
                        onClick={handlePrevious}
                        className="tw:absolute tw:left-4 tw:top-1/2 tw:-translate-y-1/2 tw:z-10 tw:w-8 tw:h-8 tw:bg-white tw:!rounded-full tw:flex tw:items-center tw:justify-center tw:!shadow-lg tw:!border-0"
                        style={{ marginTop: '-20px' }}
                    >
                        <span className="tw:text-gray-600 tw:font-bold tw:text-lg">‹</span>
                    </button>
                    <button
                        onClick={handleNext}
                        className="tw:absolute tw:right-4 tw:top-1/2 tw:-translate-y-1/2 tw:z-10 tw:w-8 tw:h-8 tw:bg-white tw:!rounded-full tw:flex tw:items-center tw:justify-center tw:!shadow-lg tw:!border-0"
                        style={{ marginTop: '-20px' }}
                    >
                        <span className="tw:text-gray-600 tw:font-bold tw:text-lg">›</span>
                    </button>
                </>
            )}

            {/* Carousel Cards */}
            <div className="tw:flex tw:gap-2 tw:w-full tw:flex-1">
                {visibleAssets.map((asset, index) => {
                    const assetVariant = asset.variations.find((variant) => variant.format === 'square')
                    const thisCropData = assetVariant.crop_data
                    return (
                        <div
                            key={asset?.id || index}
                            className="tw:flex-1 tw:bg-white tw:overflow-hidden tw:!shadow-sm tw:flex tw:flex-col"
                        >
                            {/* Image */}
                            <div className="tw:relative tw:flex-1 tw:min-h-[100px] tw:overflow-hidden">
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        aspectRatio: '1/1',
                                        minWidth: '100%',
                                        minHeight: '100%',
                                        backgroundColor: '#3B5168',
                                    }}
                                >
                                    <Media
                                        type={asset?.type}
                                        url={asset?.url || ''}
                                        aspect={thisCropData?.ratio || '1:1'}
                                        canvasWidth={assetVariant.cropped ? thisCropData?.width : 241}
                                        canvasHeight={assetVariant.cropped ? thisCropData?.height : 241}
                                        cropData={assetVariant.cropped ? thisCropData : null}
                                        isProxied={true}
                                        autoStart={autoPlay || playWithSound}
                                        startMuted={!playWithSound}
                                        startTime={asset?.trim_data?.start}
                                        endTime={asset?.trim_data?.end}
                                        thumbnails={asset?.thumbnails}
                                    />
                                </div>
                            </div>

                            {/* Card Content */}
                            <div
                                className="d-flex justify-content-between align-items-start tw:pt-4 px-3 border-top border-gray-700 bg-gray-100 tw:flex-none"
                                style={{ minHeight: '120px' }}
                            >
                                <div className="tw:flex tw:flex-col tw:gap-1 tw:min-w-0 tw:overflow-hidden">
                                    <div className="tw:text-sm tw:font-semibold tw:text-gray-900 tw:truncate tw:mb-0.5 tw:leading-tight overflow-ellipse tw:whitespace-nowrap tw:max-w-[175px]">
                                        {asset?.headline}
                                    </div>
                                    <div className="tw:text-xs tw:font-normal tw:text-gray-900 tw:truncate tw:leading-tight tw:mb-2 tw:w-[175px]">
                                        {asset?.description}
                                    </div>
                                </div>
                                <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
                                    <button
                                        className="tw:self-start tw:text-black tw:px-3 tw:py-2 tw:!mb-5 tw:!rounded-sm tw:!text-xs tw:!font-bold tw:!border-0 tw:whitespace-nowrap"
                                        style={{
                                            backgroundColor: '#e4e6ea',
                                            color: '#1c1e21',
                                            minWidth: '70px',
                                        }}
                                    >
                                        {getCTALabel(cta)}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export const AdPreviewSquare = ({
    adCreativeFormat,
    assetVariations,
    primaryText = '',
    headline = '',
    description = '',
    cta = 'Learn More',
    pageTitle = 'Page Name',
    pageIcon = iconPlaceholder,
    showInstantForm = false,
    displayLink = '',
    destinationUrl = '',
    showDefaultPrimaryText = false,
    carouselAssets = [],
    size = 'default',
    assetData = {},
    mockPreview = false,
    cropStateFromMediaCrop = [],
    assetVariant: assetVariantProp,
    autoPlay = false,
    playWithSound = false,
}) => {
    // Use first variation if arrays are passed and apply truncation
    const rawPrimaryText = Array.isArray(primaryText) ? primaryText[0] : primaryText
    const rawHeadline = Array.isArray(headline) ? headline[0] : headline
    const rawDescription = Array.isArray(description) ? description[0] : description

    // Use assetVariant prop when passed (e.g. CreativeCard), else derive from assetVariations
    const [derivedVariant, setDerivedVariant] = useState()
    useEffect(() => {
        if (assetVariations && !assetVariantProp) {
            setDerivedVariant(assetVariations.find((x) => x.format === 'square'))
        }
    }, [assetVariations, assetVariantProp])
    const thisAssetVariant = assetVariantProp ?? derivedVariant

    // Determine what to show in the footer URL area
    const getDisplayUrl = () => {
        if (showInstantForm) {
            return 'FORM'
        }
        // Show Display Link if filled, otherwise show Destination URL
        return displayLink.trim() || destinationUrl.trim() || ''
    }

    const {
        url: effectiveUrl,
        cropData: effectiveCropData,
        canvasWidth: effectiveCanvasWidth,
        canvasHeight: effectiveCanvasHeight,
    } = getFormatMediaProps({
        format: 'square',
        cropStateFromMediaCrop,
        assetData,
        assetVariant: thisAssetVariant,
        defaultWidth: 241,
        defaultHeight: 241,
    })

    const isCarousel = adCreativeFormat === 'carousel' && !mockPreview

    return (
        <div className="d-flex justify-content-start align-items-center overflow-x-hidden">
            <div
                className={cn(
                    'tw:!max-w-[305px] tw:!w-[305px] phone-mockup border border-gray-700 rounded tw:flex tw:flex-col text-dark tw:max-h-[600px] tw:overflow-hidden tw:!relative',
                    isCarousel ? 'tw:h-[540px]' : 'tw:min-h-[540px]',
                )}
            >
                {/* Header */}
                <div className="d-flex align-items-center pt-2 pb-1 ps-1 pe-3 tw:flex-shrink-0">
                    <LazyAvatar src={pageIcon} placeholder={iconPlaceholder} alt={'Page Image'} />

                    <div className="w-100" style={{ transform: 'translateY(10px)' }}>
                        <div className="ms-2 text-dark2">{pageTitle}</div>
                        <div className="ms-2 fs-8 text-muted d-flex w-100" style={{ transform: 'translateY(-3px)' }}>
                            <span>Sponsored</span>
                            <span className="ps-2 pe-2 fs-4" style={{ transform: 'translateY(-13px)' }}>
                                .
                            </span>
                            <img src={sponsorIcon} alt="sponsor Icon" className="icon-xxs mt-1" />
                            <span className="ms-auto fs-5" style={{ transform: 'translateY(-20px) translateX(-15px)' }}>
                                ...
                            </span>
                        </div>
                    </div>
                </div>
                {/* Primary Text */}
                <div className="flex align-items-start px-3 tw:pb-1 tw:relative tw:flex-shrink-0">
                    <span className="fs-9 text-dark2 tw:break-all overflow-ellipse tw:min-h-[20px] tw:max-w-[270px] tw:max-h-[120px]">
                        {showDefaultPrimaryText
                            ? 'Dreaming of a carefree beach escape?'
                            : ellipses(rawPrimaryText, 125, false) || ''}
                    </span>
                    {rawPrimaryText?.length >= 125 && (
                        <span className="fs-9 tw:text-slate-500 tw:font-bold tw:absolute tw:right-3 tw:bottom-1 tw:bg-white tw:pr-1 tw:pl-4 ">
                            ... see more
                        </span>
                    )}
                </div>
                {/* Media area */}
                {isCarousel ? (
                    <CarouselPreview
                        cta={cta}
                        headline={rawHeadline}
                        assets={carouselAssets}
                        description={rawDescription}
                        primaryText={rawPrimaryText}
                        autoPlay={autoPlay}
                        playWithSound={playWithSound}
                    />
                ) : (
                    <div className="tw:aspect-square tw:flex-none tw:w-full bg-black position-relative mock-phone-center tw:flex tw:items-center tw:justify-center tw:overflow-hidden">
                        {effectiveUrl ? (
                            <div
                                style={{
                                    overflow: 'hidden',
                                    borderRadius: 2,
                                    backgroundColor: '#3B5168',
                                    width: '100%',
                                    height: '100%',
                                    position: 'relative',
                                }}
                                className=" tw:relative"
                            >
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        aspectRatio: '1/1',
                                        minWidth: '100%',
                                        minHeight: '100%',
                                    }}
                                >
                                    <Media
                                        type={assetData?.type}
                                        url={effectiveUrl}
                                        thumbnails={assetData?.thumbnails}
                                        canvasWidth={effectiveCanvasWidth}
                                        canvasHeight={effectiveCanvasHeight}
                                        cropData={effectiveCropData}
                                        isProxied={true}
                                        autoStart={autoPlay || playWithSound}
                                        startMuted={!playWithSound}
                                        startTime={assetData?.trim_data?.start}
                                        endTime={assetData?.trim_data?.end}
                                    />
                                </div>
                            </div>
                        ) : // Show format-specific default images when no media
                        adCreativeFormat === 'singleImage' ? (
                            <img src={centerMockPhone} alt="Preview" className="w-100 h-100 object-fit-cover" />
                        ) : adCreativeFormat === 'carousel' ? (
                            <img src={carouselMockPhone} alt="Preview" className="w-100 h-100 object-fit-cover" />
                        ) : adCreativeFormat === 'video' ? (
                            <img src={videoMockPhone} alt="Preview" className="w-100 h-100 object-fit-cover" />
                        ) : (
                            // Fallback if format is not recognized
                            <div className="w-100 h-100 d-flex align-items-center justify-content-center text-white-50">
                                Preview
                            </div>
                        )}

                        {/* Carousel indicator - only show for actual carousels with media */}
                        {adCreativeFormat === 'carousel' > 0 && (
                            <div
                                className="position-absolute"
                                style={{
                                    bottom: '8px',
                                    right: '8px',
                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                }}
                            >
                                <span className="text-white" style={{ fontSize: '12px' }}>
                                    1/{carouselAssets.length || 5}
                                </span>
                            </div>
                        )}
                    </div>
                )}
                {/* Footer */}
                {adCreativeFormat !== 'carousel' && (
                    <div
                        className="d-flex justify-content-between align-items-start pt-3 py-3 px-3 border-top border-gray-700 bg-gray-100 tw:flex-1"
                        style={{ minHeight: '140px' }}
                    >
                        {showDefaultPrimaryText ? (
                            // Default content for SelectFormat
                            <>
                                <span className="text-dark2 fs-7">Sun &amp; Surf Awaits</span>
                                <img src={bookNowMockPhone} alt="Book Now" className="mockPhoneNowButton" />
                            </>
                        ) : (
                            // Dynamic content
                            <>
                                <div className="flex-grow-1 tw:min-w-0 tw:!content-start">
                                    {getDisplayUrl() && (
                                        <div
                                            className="text-muted tw:align-self-top overflow-ellipse tw:whitespace-nowrap tw:max-w-[155px]"
                                            style={{ fontSize: '12px', marginBottom: '4px' }}
                                        >
                                            {getDisplayUrl()}
                                        </div>
                                    )}
                                    <div className="text-dark2 fs-7 fw-bold overflow-ellipse tw:whitespace-nowrap tw:max-w-[155px]">
                                        {rawHeadline || 'Headline'}
                                    </div>
                                    {rawDescription ? (
                                        <div
                                            className="text-muted overflow-ellipse tw:whitespace-nowrap tw:max-w-[155px]"
                                            style={{ fontSize: '12px', marginTop: '2px' }}
                                        >
                                            {rawDescription}
                                        </div>
                                    ) : (
                                        <div className="tw:h-[30px]"></div>
                                    )}
                                </div>
                                <button
                                    className="tw:!mt-2  tw:self-start tw:text-black tw:px-3 tw:py-2 tw:!mb-5 tw:!rounded-sm tw:!text-xs tw:!font-bold tw:!border-0 tw:whitespace-nowrap"
                                    style={{
                                        backgroundColor: '#e4e6ea',
                                        color: '#1c1e21',
                                        minWidth: '70px',
                                    }}
                                >
                                    {getCTALabel(cta)}
                                </button>
                            </>
                        )}
                    </div>
                )}
                {/* Engagement bar */}
                <div style={{ position: 'absolute', bottom: '0px' }}>
                    {size !== 'compact' && (
                        <>
                            <div className="tw:bg-white tw:px-1">
                                <img src={shareLikeMockPhone} alt="Share and like" />
                            </div>
                            <hr
                                className="border-gray-700"
                                style={{
                                    height: '1px',
                                    color: 'gray',
                                    marginTop: 0,
                                    marginBottom: 0,
                                }}
                            />
                        </>
                    )}
                    <img src={bottomBarMockPhone} alt="comment" className="p-2" />
                </div>
            </div>
        </div>
    )
}
